import React, { useEffect, useState } from 'react'
import Navbar from '../Home/shared/Navbar'
import SalesFloorCard from './shared/SalesFloorCard'
import SalesFloorBanner from './shared/SalesFloorBanner';
import { SalesFloorService } from '../../services/SalesFloorService'
import { useLocalStorage } from '../../auth/useLocalStorage'
import { usePusher } from '../../context/pusherContext'
import '../Home/index.css'
import { type IEvent, type Orders } from '../../common/interfaces'
import { ListModal } from '../Home/shared/ListModal'
import NotificationSound from "../Home/NotificationSound";
import { Nav } from 'react-bootstrap';

const SalesFloorIndex = () => {
  const pusher = usePusher()
  const [user] = useLocalStorage('user', null)
  const [orders, setOrders] = useState<Orders[]>([])
  const [display, setDisplay] = useState(false)
  const [update, setUpdate] = useState(1)
  const [notifications, setNotifications] = useState<{ id: string; message: string; type: string, order_id: number }[]>([]);

  const [schedules, setSchedules] = useState(false)
  const [activeTab, setActiveTab] = useState('active')
  const displayStatus = ['Pending', 'Preparing', 'Schedule', 'Shipped']
  const openList = (schedules?:boolean) => {
    setSchedules(schedules ?? false)
    setUpdate(update + 1)
    setDisplay(true)
  }
  const [allOrders, setAllOrders] = useState<Orders[]>([])

  // Establecer el título de la pestaña para la pantalla de piso de venta
  useEffect(() => {
    document.title = "Piso de Venta - La Bifería";
  }, []);

  const getOrders = async (maxRetries: number = 1) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log('🔍 [SALES FLOOR] Fetching orders from backend with display_type=floor...');
        const res = await SalesFloorService.show(user.kitchen_id as string);

        console.log('📦 [SALES FLOOR] Backend response:', res);

        if (res.success && res.object?.orders) {
          // El backend ya filtra las órdenes correctamente con display_type=floor
          // No necesitamos filtrar aquí, usamos todas las órdenes que vienen del API
          const salesFloorOrders = res.object.orders;

          console.log(`📊 [SALES FLOOR] Total orders received from backend: ${salesFloorOrders.length}`);
          console.log('📋 [SALES FLOOR] Order IDs received:', salesFloorOrders.map(o => o.id));

          // Log detallado de cada pedido
          salesFloorOrders.forEach(order => {
            console.log(`\n🔍 [ORDER #${order.id}] Details:`, {
              id: order.id,
              status: order.status,
              kitchen_status: order.kitchen_status,
              floor_status: order.floor_status,
              shipped_type: order.shipped_type,
              origin_by: order.origin_by,
              total_products: order.detalles?.length || 0,
              products_with_grilling: order.detalles?.filter(d => d.has_grilling_service).length || 0
            });

            // Log de cada producto
            order.detalles?.forEach((detalle, idx) => {
              console.log(`  📦 Product ${idx + 1}:`, {
                id: detalle.id,
                name: detalle.producto?.name,
                has_grilling_service: detalle.has_grilling_service,
                options: detalle.options
              });
            });
          });

          // Guardar todos los pedidos de piso de venta
          setAllOrders(salesFloorOrders);

          // Filtrar según la pestaña activa usando floor_status
          if (activeTab === 'active') {
            const activeOrders = salesFloorOrders.filter(order => {
              const floorStatus = order.floor_status || order.status;
              const shouldShow = displayStatus.includes(floorStatus) && floorStatus !== 'Shipped';
              console.log(`🔍 [ORDER #${order.id}] Active tab filter: floorStatus=${floorStatus}, shouldShow=${shouldShow}`);
              // Excluir pedidos que ya están entregados en piso de venta
              return shouldShow;
            });
            console.log(`✅ [SALES FLOOR] Active orders after filter: ${activeOrders.length}`, activeOrders.map(o => o.id));
            setOrders(activeOrders);
          } else if (activeTab === 'done') {
            const doneOrders = salesFloorOrders.filter(order => {
              const floorStatus = order.floor_status || order.status;
              const shouldShow = floorStatus === 'Done' && order.shipped_type !== 'shipped';
              console.log(`🔍 [ORDER #${order.id}] Done tab filter: floorStatus=${floorStatus}, shouldShow=${shouldShow}`);
              // Excluir pedidos que ya están entregados en piso de venta
              // Excluir pedidos a domicilio que están Done (se entregan desde caja)
              return shouldShow;
            });
            console.log(`✅ [SALES FLOOR] Done orders after filter: ${doneOrders.length}`, doneOrders.map(o => o.id));
            setOrders(doneOrders);
          }

          setUpdate(prev => prev + 1);
          return; // Salir si fue exitoso
        } else {
          console.warn('⚠️ [SALES FLOOR] No orders in response or response not successful');
        }

        // Si no hay órdenes y no es el último intento, esperar antes del siguiente
        if (attempt < maxRetries) {
          console.log(`⏳ [SALES FLOOR] Retrying in 2 seconds... (attempt ${attempt}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error(`❌ [SALES FLOOR] Error fetching sales floor orders (attempt ${attempt}/${maxRetries}):`, error);
        if (attempt < maxRetries) {
          console.log(`⏳ [SALES FLOOR] Retrying in 2 seconds after error...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    console.warn('⚠️ [SALES FLOOR] Failed to fetch orders after all retries');
  }

  const generateID = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  const handleApprove = (notification: { id: string; message: string; type: string, order_id: number }) => {
    SalesFloorService.setAppOrderStatus(notification.order_id.toString(), { status: 'Approved' }).then(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      getOrders();
    });
  };

  const handleReject = (notification: { id: string; message: string; type: string, order_id: number }) => {
    SalesFloorService.setAppOrderStatus(notification.order_id.toString(), { status: 'Rejected' }).then(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      getOrders();
    });
  };

  const checkIfOrderHasSalesFloorProducts = async (orderId: number, maxRetries: number = 3): Promise<boolean> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Hacer una consulta fresca para obtener el pedido específico
        const res = await SalesFloorService.show(user.kitchen_id as string);
        if (res.success && res.object?.orders) {
          const order = res.object.orders.find(o => o.id === orderId);
          if (order) {
            // El backend ya filtra con display_type=floor, si la orden está aquí, es porque tiene productos de piso
            return true;
          }
        }

        // Si no encontramos el pedido y no es el último intento, esperar antes del siguiente
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error(`Error checking order products (attempt ${attempt}):`, error);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    return false;
  };

  useEffect(() => {
    getOrders()
    const channel = pusher.subscribe('kitchen-channel')

    // Escuchar nuevos pedidos y actualizaciones
    channel.bind('new-order', async (order: IEvent) => {
      if (order.success && order.object && order.object.kitchen_id === user.kitchen_id) {
        if (order.object.type == 'ORDER_APPROVAL') {
          // Las notificaciones de aprobación deben aparecer en ambas pantallas
          setNotifications((prev) => [
            ...prev,
            { id: generateID(), message: order.object.message, type: order.object.type, order_id: order.object.order_id}
          ]);
        } else if (order.object.type == 'ORDER_STATUS_UPDATE') {
          // Actualización de estado de pedido - solo refrescar sin notificación
          console.log('Sales floor screen: Order status update received, refreshing orders...', order.object);
          await getOrders();
        } else {
          // Usar reintentos para actualizar la lista de pedidos y mostrar notificación
          setTimeout(async () => {
            // Actualizar la lista con reintentos
            await getOrders(3);

            // Verificar si el pedido tiene productos de piso de venta
            const hasSalesFloorProducts = await checkIfOrderHasSalesFloorProducts(order.object.order_id);
            if (hasSalesFloorProducts) {
              setNotifications((prev) => [
                ...prev,
                { id: generateID(), message: "<h1>Se ha recibido un nuevo Pedido</h1>", type: 'MESSAGE', order_id: order.object.order_id}
              ]);
            }
          }, 1000);
        }
      }
    });

    // Escuchar cambios de estado de pedidos
    channel.bind('order-status-changed', async (data: any) => {
      if (data.success && data.object && data.object.kitchen_id === user.kitchen_id) {
        if (data.object.type === 'PAYMENT_STATUS_UPDATE') {
          console.log('Sales floor screen: Payment status updated, refreshing orders...', data.object);
          // Actualizar inmediatamente cuando se actualiza el estado de pago
          await getOrders();
        } else {
          console.log('Sales floor screen: Order status changed, refreshing orders...', data.object);
          await getOrders();
        }
      }
    });

    return () => {
      channel.unbind('new-order');
      channel.unbind('order-status-changed');
    };
  }, [pusher, user.kitchen_id])

  // Efecto para actualizar los pedidos cuando cambia la pestaña activa
  useEffect(() => {
    if (allOrders.length > 0) {
      if (activeTab === 'active') {
        const filteredOrders = allOrders.filter(order => {
          const floorStatus = order.floor_status || order.status;
          // Excluir pedidos que ya están entregados en piso de venta
          return displayStatus.includes(floorStatus) && floorStatus !== 'Shipped';
        });
        setOrders(filteredOrders);
      } else if (activeTab === 'done') {
        const filteredOrders = allOrders.filter(order => {
          const floorStatus = order.floor_status || order.status;
          // Excluir pedidos que ya están entregados en piso de venta
          // Excluir pedidos a domicilio que están Done (se entregan desde caja)
          return floorStatus === 'Done' && order.shipped_type !== 'shipped';
        });
        setOrders(filteredOrders);
      }
    }
  }, [activeTab, allOrders])

  return (
    <>
      <Navbar openList={openList}/>
      <ListModal display={display} setDisplay={setDisplay} key={update} schedules={schedules}/>

      {notifications.map((notification, index) => (
        <NotificationSound
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          count={index + 1}
          total={notifications.length}
          onClose={() => setNotifications((prev) => prev.filter((n) => n.id !== notification.id))}
          onApprove={() => handleApprove(notification)}
          onReject={() => handleReject(notification)}
        />
      ))}

      <SalesFloorBanner
        orders={activeTab === 'active' ? orders : allOrders.filter(order => {
          const floorStatus = order.floor_status || order.status;
          return displayStatus.includes(floorStatus);
        })}
        totalOrders={orders.length}
      />

      <div className="container-fluid">
        <Nav
          variant="tabs"
          className="mb-3"
          activeKey={activeTab}
          style={{
            borderBottom: '2px solid #6c757d',
            paddingBottom: '0px',
            marginBottom: '20px'
          }}
          onSelect={(k) => {
            if (k) {
              setActiveTab(k)
              // Actualizar los pedidos mostrados según la pestaña seleccionada
              if (k === 'active') {
                setOrders(allOrders.filter(order => {
                  const floorStatus = order.floor_status || order.status;
                  // Excluir pedidos que ya están entregados en piso de venta
                  return displayStatus.includes(floorStatus) && floorStatus !== 'Shipped';
                }))
              } else if (k === 'done') {
                setOrders(allOrders.filter(order => {
                  const floorStatus = order.floor_status || order.status;
                  // Excluir pedidos que ya están entregados en piso de venta
                  // Excluir pedidos a domicilio que están Done (se entregan desde caja)
                  return floorStatus === 'Done' && order.shipped_type !== 'shipped';
                }))
              }
            }
          }}
        >
          <Nav.Item>
            <Nav.Link
              eventKey="active"
              className={activeTab === 'active' ? 'bg-primary text-white' : 'text-white'}
              style={{
                borderRadius: '8px 8px 0 0',
                fontWeight: 'bold',
                fontSize: '16px',
                padding: '10px 20px',
                margin: '0 5px'
              }}
            >
              Pedidos Activos <span className="badge bg-light text-primary ms-2" style={{ fontSize: '14px' }}>
                {allOrders.filter(order => {
                  const floorStatus = order.floor_status || order.status;
                  // Excluir pedidos que ya están entregados en piso de venta
                  return displayStatus.includes(floorStatus) && floorStatus !== 'Shipped';
                }).length}
              </span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="done"
              className={activeTab === 'done' ? 'bg-success text-white' : 'text-white'}
              style={{
                borderRadius: '8px 8px 0 0',
                fontWeight: 'bold',
                fontSize: '16px',
                padding: '10px 20px',
                margin: '0 5px'
              }}
            >
              Pedidos Terminados <span className="badge bg-light text-success ms-2" style={{ fontSize: '14px' }}>
                {allOrders.filter(order => {
                  const floorStatus = order.floor_status || order.status;
                  // Excluir pedidos a domicilio que están Done (se entregan desde caja)
                  return floorStatus === 'Done' && order.shipped_type !== 'shipped';
                }).length}
              </span>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <div className="row g-3">
          {
            orders.map((item) => (
              <div key={item.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <SalesFloorCard {...item} className="w-100" updateCallback={getOrders}/>
              </div>
            ))
          }
          {
            orders.length === 0 && (
              <div className="col-12">
                <h3 style={{ textAlign: 'center', color: 'white' }}>
                  {activeTab === 'active' ? 'No hay pedidos pendientes' : 'No hay pedidos terminados'}
                </h3>
              </div>
            )
          }
        </div>
      </div>
    </>
  )
}

export default SalesFloorIndex
